import React from "react";

type ExperienceStat = {
  value: string;
  label: string;
  seoLabel: string;
};

type FloatingCard = {
  title: string;
  desc: string;
};

const experienceStats: ExperienceStat[] = [
  {
    value: "10+",
    label: "Years",
    seoLabel: "10+ years of quality education support",
  },
  {
    value: "150+",
    label: "Live Classes",
    seoLabel: "150+ live online classes for school, JEE and NEET students",
  },
  {
    value: "4.9",
    label: "Rating",
    seoLabel: "4.9 student rating for online learning experience",
  },
  {
    value: "24/7",
    label: "Support",
    seoLabel: "24/7 student support for classes, notes, tests and doubts",
  },
];

const floatingCards: FloatingCard[] = [
  {
    title: "Live Classes",
    desc: "Daily mentor-led learning",
  },
  {
    title: "Mock Tests",
    desc: "Practice with reports",
  },
];

const ExperienceBanner: React.FC = () => {
  const experienceSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Mediit Academy",
    description:
      "Mediit Academy is an online learning platform for Class 7 to 12 students, JEE Main aspirants and NEET UG aspirants with live classes, study notes, mock tests, doubt solving and performance tracking.",
    url: "/",
    slogan:
      "Online courses for Class 7 to 12, JEE Main, NEET UG and board exam preparation",
    knowsAbout: [
      "Class 7 online classes",
      "Class 8 online tuition",
      "Class 9 foundation course",
      "Class 10 board exam preparation",
      "Class 12 board exam preparation",
      "JEE Main online coaching",
      "NEET UG online coaching",
      "Live online classes",
      "Mock test series",
      "Study notes",
      "Doubt solving support",
    ],
    offers: {
      "@type": "OfferCatalog",
      name: "Online Education Programs",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "School Foundation Courses",
            description:
              "Foundation learning for Class 7 to 10 students with Maths, Science, Reasoning, notes, tests and live classes.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "Board Exam Preparation",
            description:
              "Class 10 and Class 12 board exam preparation with NCERT revision, sample papers and mock tests.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "JEE Main Preparation",
            description:
              "JEE Main online coaching for Physics, Chemistry and Mathematics with live classes and test series.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "NEET UG Preparation",
            description:
              "NEET UG online coaching for Physics, Chemistry and Biology with NCERT-focused learning and performance tracking.",
          },
        },
      ],
    },
  };

  return (
    <section
      id="experience-banner"
      className="relative overflow-hidden bg-[#eaf8ff] py-14 font-['Inter'] sm:py-16 lg:py-20"
      aria-labelledby="experience-banner-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(experienceSchema),
        }}
      />

      <div className="absolute left-10 top-10 h-40 w-40 rounded-full bg-blue-200/50 blur-3xl" />
      <div className="absolute bottom-10 right-10 h-44 w-44 rounded-full bg-orange-200/50 blur-3xl" />

      <div className="relative mx-auto grid max-w-[1380px] grid-cols-1 items-center gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8 xl:px-4">
        {/* LEFT IMAGE SIDE */}
        <div className="order-2 lg:order-1 lg:col-span-6">
          <div className="relative mx-auto h-[360px] max-w-[560px] sm:h-[430px] lg:h-[460px]">
            <div className="absolute left-1/2 top-1/2 h-[270px] w-[270px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#c9f4ff]" />
            <div className="absolute left-1/2 top-1/2 h-[190px] w-[190px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#b6efff]" />
            <div className="absolute left-1/2 top-1/2 h-[92px] w-[92px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[18px] border-emerald-500 bg-white" />

            <img
              src="https://www.vidyalayalms.com/images/whyvidayalaya/whyus_banner.png"
              alt="Mediit Academy online learning platform for live classes mock tests study notes and doubt solving"
              loading="lazy"
              decoding="async"
              width={480}
              height={340}
              className="absolute left-1/2 top-1/2 h-[300px] w-[420px] -translate-x-1/2 -translate-y-1/2 sm:h-[340px] sm:w-[480px]"
            />

            {/* Floating cards */}
            <div className="absolute left-2 top-8 rounded-2xl bg-white px-4 py-3 shadow-xl sm:left-8">
              <p className="text-sm font-black text-slate-950">
                {floatingCards[0].title}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {floatingCards[0].desc}
              </p>
            </div>

            <div className="absolute right-2 top-16 rounded-2xl bg-white px-4 py-3 shadow-xl sm:right-8">
              <p className="text-sm font-black text-slate-950">
                {floatingCards[1].title}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {floatingCards[1].desc}
              </p>
            </div>

            <div className="absolute bottom-4 left-1/2 flex w-[280px] -translate-x-1/2 items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/95 px-4 py-3 shadow-xl backdrop-blur-sm">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-xl text-white"
                aria-hidden="true"
              >
                ✓
              </div>

              <div>
                <p className="text-sm font-black text-slate-950">
                  Trusted Learning Platform
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Classes + notes + tests
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT SIDE */}
        <div className="order-1 lg:order-2 lg:col-span-6">
          <div className="max-w-2xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
              Trusted Online Learning Platform
            </p>

            <h2
              id="experience-banner-heading"
              className="mt-4 text-4xl font-black leading-tight tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-[56px]"
            >
              10+ Years of Quality Education Support for School, JEE and NEET
              Students
            </h2>

            <p className="mt-5 text-base leading-7 text-slate-700 sm:text-lg">
              Mediit Academy helps Class 7, Class 8, Class 9, Class 10, Class
              11, Class 12, JEE Main aspirants and NEET UG aspirants prepare
              with structured live online classes, expert mentors, study notes,
              doubt solving support, mock test series and performance tracking —
              all in one simple LMS platform.
            </p>

            <p className="sr-only">
              Best online learning platform for school students, Class 10 board
              preparation, Class 12 board preparation, JEE Main online coaching,
              NEET UG online coaching, live classes, study notes, mock tests,
              assignments and doubt solving support.
            </p>

            <div
              className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4"
              aria-label="Mediit Academy experience and learning platform highlights"
            >
              {experienceStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl bg-white p-4 text-center shadow-sm"
                  title={stat.seoLabel}
                >
                  <p className="text-3xl font-black text-slate-950">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    {stat.label}
                  </p>
                  <p className="sr-only">{stat.seoLabel}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="/courses"
                aria-label="Explore online courses for Class 7 to 12 JEE Main NEET UG and board exam preparation"
                className="rounded-xl bg-[#101827] px-7 py-3 text-center text-sm font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800"
              >
                Explore Courses
              </a>

              <a
                href="/login"
                aria-label="Login and start learning with live classes notes tests and doubt support"
                className="rounded-xl border border-slate-300 bg-white px-7 py-3 text-center text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5 hover:bg-slate-50"
              >
                Start Learning
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceBanner;